import React, { useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import { breakdownLines, countWords } from './predictSbomSources';
import './predictSbomCount.css';

const POPOVER_WIDTH = 560;
const GUTTER = 12;

/**
 * A managed product's "With an SBOM" cell (STRY0039142): how many of its hosts have an SBOM, from any
 * source, with an amber dot when some don't. Clicking it opens where each share came from in a small
 * popover beside it; Escape, a click outside or a scroll closes it. The popover is portalled so the
 * products table's scroll box never clips it. A product synced before `originSources` existed shows its
 * hosts only.
 */
const PredictSbomCount = ({ product, vendorName }) => {
  const origin = product.origin;
  const [open, setOpen] = useState(false);
  const [place, setPlace] = useState(null);
  const button = useRef(null);
  const card = useRef(null);
  const id = useId();

  useLayoutEffect(() => {
    if (!open || !button.current) return;
    const rect = button.current.getBoundingClientRect();
    setPlace({
      top: rect.bottom + 4,
      left: Math.max(GUTTER, Math.min(rect.left, window.innerWidth - POPOVER_WIDTH - GUTTER))
    });
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const close = () => setOpen(false);
    const outside = e => {
      if (!button.current?.contains(e.target) && !card.current?.contains(e.target)) close();
    };
    const escape = e => e.key === 'Escape' && close();
    document.addEventListener('mousedown', outside);
    document.addEventListener('keydown', escape);
    window.addEventListener('scroll', close, true);
    window.addEventListener('resize', close);
    return () => {
      document.removeEventListener('mousedown', outside);
      document.removeEventListener('keydown', escape);
      window.removeEventListener('scroll', close, true);
      window.removeEventListener('resize', close);
    };
  }, [open]);

  if (!origin) {
    return <span className="bz-muted">{product.hosts != null ? countWords(product.hosts) : '—'}</span>;
  }

  const lines = breakdownLines(origin, vendorName, product.found);
  const some = origin.covered < origin.hosts;
  return (
    <>
      <button
        ref={button}
        type="button"
        className="pc-count"
        aria-expanded={open}
        aria-controls={open ? id : undefined}
        onClick={() => setOpen(o => !o)}
      >
        <span className="bz-tnum pc-count__n">{origin.covered}</span>
        <span className="pc-count__of"> of {countWords(origin.hosts, origin.unit)}</span>
        {some && <span className="pc-count__gap" role="img" aria-label="some have no SBOM" />}
        <span className="pc-count__chev" aria-hidden="true">
          ▾
        </span>
      </button>
      {open &&
        createPortal(
          <div
            ref={card}
            id={id}
            role="dialog"
            aria-label={`Where ${product.name}’s OSS list comes from`}
            className="pc-pop"
            style={{ width: POPOVER_WIDTH, ...(place || { visibility: 'hidden' }) }}
          >
            <div className="pc-pop__title">Where {product.name}’s OSS list comes from</div>
            {lines.map(line => (
              <div key={line.code} className={`pc-pop__line${line.code === 'none' ? ' pc-pop__line--gap' : ''}`}>
                <span className="pc-pop__src">{line.source}</span>
                <span className="pc-pop__count bz-tnum">{line.count}</span>
                <span className="pc-pop__found">{line.found}</span>
              </div>
            ))}
          </div>,
          document.body
        )}
    </>
  );
};

PredictSbomCount.propTypes = {
  product: PropTypes.shape({
    name: PropTypes.string,
    hosts: PropTypes.number,
    origin: PropTypes.shape({
      unit: PropTypes.string,
      hosts: PropTypes.number,
      covered: PropTypes.number,
      sources: PropTypes.array
    }),
    found: PropTypes.object
  }).isRequired,
  vendorName: PropTypes.string
};

export default PredictSbomCount;
